import { useEffect, useRef } from "react";
import { useWatch } from "react-hook-form";

import { useCurrentComposition } from "@design-lab/entities/composition";
import type { ArtworkForm, PlacementFormPosition, Side } from "@design-lab/shared/types";

import { getEffectiveImageUrl } from "../lib/getEffectiveImageUrl";
import { calculatedArtworkDimensions } from "../lib/calculateDimensions";
import { useArtwork } from "./useArtwork";
import { useArtworkForm } from "./useArtworkForm";
import { useImagesFastCache } from "./useImagesFastCache";

const PLACEMENTS: PlacementFormPosition[] = ["front", "frontLeftChest", "back"];

function getArtworkSide(placement: PlacementFormPosition): Side {
  return placement === "back" ? "back" : "front";
}

/**
 * Ensures all placements with artwork have valid sizes calculated.
 * This hook runs globally and computes sizes for placements even when their tabs/canvases are not visible.
 * Fixes the issue where "Copy to Other Side" sets artworkId but size stays 0x0 because the canvas never rendered.
 */
export function useEnsureAllPlacementSizes() {
  const { form } = useArtworkForm();
  const { getArtwork } = useArtwork();
  const { currentComposition } = useCurrentComposition();

  const artworkForm = useWatch({
    control: form.control
  }) as ArtworkForm | undefined;

  // Get artwork data for front and back sides
  const frontArtworkId = artworkForm?.front.artworkId || "";
  const backArtworkId = artworkForm?.back.artworkId || "";

  const frontArtwork = getArtwork(frontArtworkId);
  const backArtwork = getArtwork(backArtworkId);

  // Select the effective image URL based on enhancement settings
  // (removeBackground, enhancementTier) so dimensions match the displayed image
  const frontInfo = artworkForm?.info?.front;
  const backInfo = artworkForm?.info?.back;
  const frontImageUrl = getEffectiveImageUrl(frontArtwork, frontInfo);
  const backImageUrl = getEffectiveImageUrl(backArtwork, backInfo);

  // Cache images (this works even without a rendered canvas)
  const [frontCachedImage, backCachedImage] = useImagesFastCache([frontImageUrl, backImageUrl]);

  // Track which placements we've already processed to avoid infinite loops
  const processedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!artworkForm || !currentComposition) return;

    const { surfaces } = currentComposition;

    PLACEMENTS.forEach(placement => {
      const placementData = artworkForm[placement];
      const artworkId = placementData?.artworkId;
      const size = placementData?.size;

      // Skip if no artwork or size is already valid
      const hasValidSize = size && size.width > 0 && size.height > 0;
      if (!artworkId || hasValidSize) {
        return;
      }

      const surface = surfaces[placement];
      const artworkSide = getArtworkSide(placement);
      const cachedImage = artworkSide === "front" ? frontCachedImage : backCachedImage;

      // Skip if surface or image not available yet
      if (!surface || !cachedImage?.image) {
        return;
      }

      // Create a unique key for this placement+artwork combination
      const processKey = `${placement}-${artworkId}-${currentComposition.id}`;

      // Skip if we've already processed this exact combination
      if (processedRef.current.has(processKey)) {
        return;
      }

      // Calculate dimensions
      const dimensions = calculatedArtworkDimensions(cachedImage.image, surface);

      // Only set if we got valid dimensions
      if (dimensions.maxWidthInches > 0 && dimensions.maxHeightInches > 0) {
        form.setValue(`${placement}.size`, {
          width: dimensions.maxWidthInches,
          height: dimensions.maxHeightInches
        });

        // Mark as processed
        processedRef.current.add(processKey);
      }
    });
  }, [artworkForm, currentComposition, frontCachedImage, backCachedImage, form]);

  // Clear processed set when composition changes (user switches products/colors)
  useEffect(() => {
    processedRef.current.clear();
  }, [currentComposition?.id]);
}
